import type { BulkAPIErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type { Review as IReview } from '@bulknutrients/bulk-shared-lib/interfaces/Review';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { LoadMore } from '@components/LoadMore/LoadMore';
import { type ReactElement, useEffect, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import { Review, ReviewSkeleton } from './Review';

export function ReviewList({
  productId,
}: {
  productId?: number;
}): ReactElement {
  const PAGE_SIZE = 10;
  const [showError, setShowError] = useState(false);

  // A function to get the SWR key of each page,
  // its return value will be accepted by `fetcher`.
  // If `null` is returned, the request of that page won't start.
  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.length) return null; // reached the end
    return productId
      ? `/api/reviews/product?page=${pageIndex}&pageSize=${PAGE_SIZE}&productId=${productId}&status=approved`
      : `/api/reviews?page=${pageIndex}&pageSize=${PAGE_SIZE}&status=approved`; // SWR key
  };

  const fetcher = async (url) => {
    const res = await fetch(url);

    // If no error but no more data
    if (res.status === 204) {
      // empty data
      return [];
    }

    // If not ok, then get the error
    if (res.status !== 200) {
      const {
        error: { message },
      } = (await res.json()) as BulkAPIErrorResponse;
      const error = new Error('Unable to fetch reviews');
      error.message = message.join('. ');
      throw error;
    }

    // Everything as expected
    const { data: reviews } = await res.json();
    return reviews as IReview[];
  };

  const {
    data,
    error: fetchError,
    isLoading,
    size,
    setSize,
  } = useSWRInfinite(getKey, fetcher);

  useEffect(() => {
    if (fetchError) {
      setShowError(true);
    }
  }, [fetchError]);

  const reviews: Array<IReview> = data ? [...data.flat()] : [];

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data?.[0]?.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.length < PAGE_SIZE);

  return isLoading ? (
    <div className="flex flex-col gap-10">
      {[...Array(5)].map((_, idx) => (
        <ReviewSkeleton key={`reviewListSkeleton${idx}`} />
      ))}
    </div>
  ) : (
    <>
      {showError && (
        <ErrorAlert title={fetchError.name} show={true} setShow={setShowError}>
          {fetchError.message}
        </ErrorAlert>
      )}
      {reviews.length === 0 && !fetchError && (
        <p className="text-center">No reviews available.</p>
      )}
      {reviews && (
        <div className="flex flex-col gap-10">
          {reviews.map((review) => (
            <Review key={review.id} review={review} />
          ))}
        </div>
      )}
      <LoadMore
        clickHandler={() => setSize(size + 1)}
        disabled={(isLoadingMore || isReachingEnd) ?? false}
        text={
          isLoadingMore
            ? 'loading...'
            : isReachingEnd
              ? 'no more results'
              : 'load more'
        }
        viewedResults={reviews.length ?? 0}
      />
    </>
  );
}
